import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import CHALLENGE_IMAGE from '../../../../app/assets/images/projectImage/cryoneimage/challenge_laptop.webp';
import COIN2_IMAGE from '../../../../app/assets/images/projectImage/cryoneimage/COIN2.png';

type CryoneObjectiveProps = {
    cryoneProject: any
}

const CryoneObjective = ({ cryoneProject }: CryoneObjectiveProps) => {
    const sectionRef = useRef(null)
    const isInView = useInView(sectionRef, { once: true, amount: 0.1 })
    const { cryoneProjectDetails } = cryoneProject ?? {}
    const { challengeSection } = cryoneProjectDetails ?? {}

    const fadeUp = {
        hidden: { opacity: 0, y: 36 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } }
    }

    const cardStagger = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.1, delayChildren: 0.15 }
        }
    }

    const cardItem = {
        hidden: { opacity: 0, y: 20, scale: 0.98 },
        visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.55 } }
    }

    const challenges = challengeSection?.points

    return (
        <section className='relative bg-[#141414] pt-20 sm:pt-24 pb-16 sm:pb-20 overflow-visible' ref={sectionRef}>
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_35%_50%,rgba(239,68,68,0.15),rgba(0,0,0,0)_60%)]" />

            <div className='relative max-w-[1280px] mx-auto px-4 sm:px-6'>
                <motion.div className="relative flex flex-nowrap items-center justify-center gap-2 sm:gap-4 mb-8 sm:mb-10 -translate-y-6 sm:-translate-y-12 md:-translate-y-14" variants={fadeUp} initial="hidden" animate={isInView ? 'visible' : 'hidden'}>
                    <span className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-[#B41F1F] flex-shrink-0" />
                    <h2 className="uppercase font-extrabold tracking-[1.5px] text-[28px] sm:text-[50px] md:text-[75px] leading-none">
                        {challengeSection?.title}
                    </h2>
                    <span className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-[#B41F1F] flex-shrink-0" />

                    <motion.img
                        src={COIN2_IMAGE}
                        alt="Cryone challenge coin"
                        className="pointer-events-none absolute right-2 sm:right-[6%] -top-12 sm:-top-14 md:-top-16 w-[72px] sm:w-[120px] md:w-[145px]"
                        initial={{ opacity: 0, y: 10, rotate: -8 }}
                        animate={isInView ? { opacity: 1, y: 0, rotate: -8 } : { opacity: 0, y: 10, rotate: -8 }}
                        transition={{ duration: 0.65, delay: 0.2 }}
                    />
                </motion.div>

                <div className="relative flex flex-col lg:block">
                    <motion.div variants={fadeUp} initial="hidden" animate={isInView ? 'visible' : 'hidden'} className="max-w-[920px] order-1">
                        <motion.p className="text-white/90 text-[18px] sm:text-[22px] md:text-[30px] leading-[1.35]" variants={fadeUp}>
                            {challengeSection?.intro}
                        </motion.p>

                        <motion.div className="mt-5 sm:mt-6 grid grid-cols-2 gap-3 sm:gap-4 max-w-[760px]" variants={cardStagger} initial="hidden" animate={isInView ? 'visible' : 'hidden'}>
                            {challenges?.map((item: string) => (
                                <motion.div
                                    key={item}
                                    className="relative rounded-[14px] border border-[#B41F1F]/95 bg-[#141414]/90 px-3 sm:px-5 py-2.5 sm:py-4 text-white/90 text-[11px] sm:text-[15px] md:text-[17px] leading-[1.35]"
                                    variants={cardItem}
                                    whileHover={{ y: -4, transition: { duration: 0.2 } }}
                                >
                                    <div className="absolute inset-0 rounded-[14px] bg-[radial-gradient(circle_at_20%_90%,rgba(180,31,31,0.25),rgba(20,20,20,0.12)_45%,rgba(20,20,20,0.08)_100%)]" />
                                    <div className="relative">
                                        <span className="mr-2">•</span>
                                        {item}
                                    </div>
                                </motion.div>
                            ))}
                        </motion.div>

                    </motion.div>

                    <div className="flex justify-center lg:justify-end order-2 lg:order-none mt-6 sm:mt-8">
                        <img
                            src={CHALLENGE_IMAGE}
                            alt="Cryone challenge dashboard"
                            loading="eager"
                            decoding="async"
                         className="pointer-events-none w-full max-w-[460px] sm:max-w-[520px] md:max-w-[590px] lg:max-w-[640px] 
drop-shadow-[0_20px_40px_rgba(0,0,0,0.6)] 
lg:absolute lg:right-[-8%] lg:top-[-80px] lg:-translate-x-[5%]"
                        />
                    </div>

                    <motion.p className="order-3 lg:order-none mt-6 sm:mt-8 text-white/90 text-[15px] sm:text-[22px] md:text-[30px] leading-[1.35] max-w-[760px]" variants={fadeUp} initial="hidden" animate={isInView ? 'visible' : 'hidden'}>
                        {challengeSection?.closing}
                    </motion.p>
                </div>
            </div>
        </section>
    )
}

export default CryoneObjective;
